import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { isPresent } from '@luminovo/commons';
import { formatPercentage } from '@luminovo/frontend-utils';
import { CircularProgress, styled } from '@mui/material';
import { FileTextIcon, UploadIcon, XIcon } from 'lucide-react';
import React from 'react';
import { FileRejection, DropzoneProps as ReactDropzoneProps } from 'react-dropzone';

import { colorSystem, focusVisibleRing } from '../../theme';
import { DestructiveTertiaryIconButton } from '../buttons';
import { CenteredSpinner } from '../CircularProgress';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { Link } from '../Link';
import { Message } from '../Message';
import { Text } from '../Text';

type PersistedFile = { name: string; onClick: () => void; onDelete: () => void; disabled?: boolean };

type DropzoneProps = {
    title: string;

    subHeader?: string;

    onDropAccepted: NonNullable<ReactDropzoneProps['onDropAccepted']>;

    /** Disables the dropzone and shows a loading indicator. */
    isLoading?: boolean;

    /**
     * If known, the progress of the upload as a number between 0 and 1.
     */
    progress?: number;

    disabled?: boolean;

    /** MIME types mapped to extensions, as react-dropzone takes them; `null` accepts any file. https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types */
    accept: NonNullable<ReactDropzoneProps['accept']> | null;

    multiple: NonNullable<ReactDropzoneProps['multiple']>;

    /** Rendered below the dropzone. */
    extraContent?: React.ReactNode;

    /** Files to keep listing after their upload. */
    persistentFiles?: Array<PersistedFile>;

    /** The upload prompt's icon, drawn at 32px in the dropzone's state color. `null` shows no icon. */
    icon?: IconElement;

    overrides?: {
        Container?: React.ElementType;
        Title?: React.ComponentType<React.ComponentProps<typeof Text>>;
        FileExtensionsInfo?: React.ComponentType<Pick<DropzoneProps, 'accept'>>;
        MessageInfo?: React.ComponentType<Pick<DropzoneProps, 'multiple' | 'disabled'>>;
    };
};

const LazyReactDropzone = React.lazy(async () => {
    const ReactDropzone = await import('react-dropzone');
    return ReactDropzone;
});

function joinFileExtensions(accept: NonNullable<DropzoneProps['accept']>, separator: 'and' | 'or'): string {
    const separatorMap = { and: t`and`, or: t`or` };

    const fileExtensions = Object.entries(accept).flatMap(([_, value]) => value);
    const last = fileExtensions.pop() ?? t`Unknown file type`;
    return fileExtensions.length ? `${fileExtensions.join(', ')} ${separatorMap[separator]} ${last}` : last;
}

const DefaultFileExtensionsInfo: React.FunctionComponent<Pick<DropzoneProps, 'accept'>> = ({ accept }) => {
    if (!isPresent(accept)) {
        return null;
    }

    return (
        <Text variant={'body-small'} color={colorSystem.neutral[6]}>
            <Trans>We support {joinFileExtensions(accept, 'and')} files</Trans>
        </Text>
    );
};

const DefaultMessageInfo: React.FunctionComponent<Pick<DropzoneProps, 'multiple' | 'disabled'>> = ({
    multiple,
    disabled,
}) => {
    if (disabled) {
        return (
            <Text variant={'body-small-semibold'} color={colorSystem.neutral[8]}>
                <Trans>Please remove the current files to upload new ones.</Trans>
            </Text>
        );
    }

    if (multiple) {
        return (
            <Flexbox
                gap={'4px'}
                sx={{
                    alignItems: 'baseline',
                }}
            >
                <Text variant={'body-small-semibold'} color={colorSystem.neutral[8]}>
                    <Trans>Drag & Drop files here or</Trans>
                </Text>
                <Link component="span" variant={'body-small-semibold'} style={{ cursor: undefined }}>
                    <Trans>click to upload</Trans>
                </Link>
            </Flexbox>
        );
    }

    return (
        <Flexbox
            gap={'4px'}
            sx={{
                alignItems: 'baseline',
            }}
        >
            <Text variant={'body-small-semibold'} color={colorSystem.neutral[8]}>
                <Trans>Drag & Drop file here or</Trans>
            </Text>
            <Link component="span" variant={'body-small-semibold'} style={{ cursor: undefined }}>
                <Trans>click to upload</Trans>
            </Link>
        </Flexbox>
    );
};

const UploadPrompt: React.FunctionComponent<{
    isDragActive: boolean;
    accept: DropzoneProps['accept'];
    isLoading: boolean;
    disabled: boolean;
    multiple: DropzoneProps['multiple'];
    icon: DropzoneProps['icon'];
    overrides: DropzoneProps['overrides'];
    progress?: DropzoneProps['progress'];
}> = ({ isDragActive, accept, isLoading, disabled, multiple, icon, overrides, progress }) => {
    const { FileExtensionsInfo = DefaultFileExtensionsInfo, MessageInfo = DefaultMessageInfo } = overrides ?? {};

    const state = disabled ? 'disabled' : isDragActive ? 'active' : 'default';
    const stateStyles = {
        default: {
            border: `1px dashed ${colorSystem.neutral[3]}`,
            bgcolor: colorSystem.neutral[0],
            cursor: 'pointer',
            iconColor: colorSystem.neutral[5],
        },
        active: {
            border: `1px dashed ${colorSystem.blue[5]}`,
            bgcolor: colorSystem.blue[1],
            cursor: 'pointer',
            iconColor: colorSystem.primary[6],
        },
        disabled: {
            border: `1px solid ${colorSystem.neutral[3]}`,
            bgcolor: colorSystem.neutral[1],
            cursor: 'not-allowed',
            iconColor: colorSystem.neutral[4],
        },
    };

    return (
        <Flexbox
            gap={'12px'}
            style={{ cursor: isLoading ? undefined : stateStyles[state].cursor }}
            sx={{
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '16px',
                borderRadius: '4px',
                border: stateStyles[state].border,
                bgcolor: stateStyles[state].bgcolor,
                transition: 'all 0.2s ease-in',
                boxShadow: '0 4px 8px 0 transparent',

                '&:hover': {
                    background: `linear-gradient(45deg, ${colorSystem.primary[1]} 0%, ${colorSystem.blue[1]} 100%)`,
                },
                // Sizes and colors the icon: it gets neither. The spinner sits in a span, out of reach.
                ...iconSlot(32),
                '& > svg': { color: stateStyles[state].iconColor },
            }}
        >
            {isLoading ? (
                <CircularProgress
                    value={progress === undefined ? undefined : progress * 100}
                    variant={progress === undefined ? 'indeterminate' : 'determinate'}
                />
            ) : (
                icon
            )}
            <Flexbox
                gap={'8px'}
                sx={{
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {isLoading ? (
                    <Text variant={'body-small-semibold'} color={colorSystem.neutral[8]}>
                        <Trans>Uploading... {progress ? `${formatPercentage(progress)}` : ''}</Trans>
                    </Text>
                ) : (
                    <>
                        <MessageInfo disabled={disabled} multiple={multiple} />
                        <FileExtensionsInfo accept={accept} />
                    </>
                )}
            </Flexbox>
        </Flexbox>
    );
};

const FileTypeError: React.FunctionComponent<{
    fileRejections: readonly FileRejection[];
    accept: DropzoneProps['accept'];
}> = ({ fileRejections, accept }) => {
    const firstRejectedFile = fileRejections.find((fileRejection) =>
        fileRejection.errors.some((error) => error.code === 'file-invalid-type'),
    );

    if (!isPresent(firstRejectedFile) || !isPresent(accept)) {
        return null;
    }

    return (
        <Message
            variant="red"
            size="large"
            attention="high"
            title={t`File type not supported`}
            message={t`${firstRejectedFile.file.name} is not supported. Please use a ${joinFileExtensions(
                accept,
                'or',
            )} file instead.`}
        />
    );
};

const FileLimitError: React.FunctionComponent<{ fileRejections: readonly FileRejection[] }> = ({ fileRejections }) => {
    const firstRejectedFile = fileRejections.find((fileRejection) =>
        fileRejection.errors.some((error) => error.code === 'too-many-files'),
    );

    if (!isPresent(firstRejectedFile)) {
        return null;
    }

    return (
        <Message
            variant="red"
            size="small"
            attention="high"
            title={t`File Limit Exceeded`}
            message={t`Please upload only one file at a time.`}
        />
    );
};

const PersistentFiles: React.FunctionComponent<{ persistentFiles: DropzoneProps['persistentFiles'] }> = ({
    persistentFiles,
}) => {
    if (!isPresent(persistentFiles) || persistentFiles.length === 0) {
        return null;
    }

    return (
        <Flexbox
            gap={'12px'}
            sx={{
                flexDirection: 'column',
                marginTop: 1,
            }}
        >
            {persistentFiles?.map(({ name, onClick, onDelete, disabled }) => (
                <Flexbox
                    key={name}
                    gap={'20px'}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Link
                        component="button"
                        startIcon={<FileTextIcon />}
                        attention="low"
                        onClick={onClick}
                        textStyle={{ overflowWrap: 'anywhere' }}
                    >
                        {name}
                    </Link>
                    <DestructiveTertiaryIconButton
                        disabled={disabled}
                        size={'small'}
                        onClick={onDelete}
                        aria-label={t`Delete ${name}`}
                    >
                        <XIcon />
                    </DestructiveTertiaryIconButton>
                </Flexbox>
            ))}
        </Flexbox>
    );
};

export const Dropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
    (
        {
            title,
            subHeader,
            extraContent,
            persistentFiles,
            onDropAccepted,
            accept,
            multiple,
            isLoading = false,
            progress,
            disabled = false,
            icon = <UploadIcon />,
            overrides = {},
        },
        ref,
    ) => {
        const { Container = DefaultContainer, Title = DefaultTitle } = overrides;
        return (
            <React.Suspense
                fallback={
                    <CenteredSpinner
                        sx={{
                            minHeight: '300px',
                        }}
                    />
                }
            >
                <LazyReactDropzone
                    onDropAccepted={onDropAccepted}
                    accept={isPresent(accept) ? accept : undefined}
                    multiple={multiple}
                    disabled={isLoading || disabled}
                >
                    {({ getRootProps, getInputProps, isDragActive, fileRejections }) => (
                        <Container ref={ref}>
                            <Flexbox
                                sx={{
                                    flexDirection: 'column',
                                }}
                            >
                                {title && <Title>{title}</Title>}
                                {isPresent(subHeader) && (
                                    <Text
                                        variant={'body-small'}
                                        color={colorSystem.neutral[6]}
                                        style={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {subHeader}
                                    </Text>
                                )}
                            </Flexbox>

                            <DropArea {...getRootProps()}>
                                <input data-testid="dropzone-input" {...getInputProps()} />
                                <UploadPrompt
                                    isDragActive={isDragActive}
                                    accept={accept}
                                    isLoading={isLoading}
                                    disabled={disabled}
                                    multiple={multiple}
                                    icon={icon}
                                    overrides={overrides}
                                    progress={progress}
                                />
                            </DropArea>
                            <FileTypeError fileRejections={fileRejections} accept={accept} />
                            <FileLimitError fileRejections={fileRejections} />
                            <PersistentFiles persistentFiles={persistentFiles} />
                            {extraContent}
                        </Container>
                    )}
                </LazyReactDropzone>
            </React.Suspense>
        );
    },
);

/** The area react-dropzone makes focusable; its radius is the prompt's, so the ring follows the prompt's corners. */
const DropArea = styled('div')({ position: 'relative', borderRadius: '4px' }, focusVisibleRing);

const DefaultContainer = styled(Flexbox)({
    width: '600px',
    gap: '16px',
    padding: '32px',
    borderRadius: '12px',
    backgroundColor: colorSystem.neutral.white,
    flexDirection: 'column',
    height: 'fit-content',
});

const DefaultTitle: React.FunctionComponent<React.PropsWithChildren<{}>> = ({ children }) => {
    return <Text variant="h3">{children}</Text>;
};
